import { useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '@crm-src/lib/store'
import {
  fetchInvoices,
  createInvoice,
  updateInvoice,
  deleteInvoice,
  bulkDeleteInvoices,
  uploadInvoiceFile,
  markInvoiceAsPaid,
  markInvoiceAsUnpaid,
  fetchInvoiceStats,
  clearCache,
  clearError,
  type Invoice,
  type InvoiceListFilters
} from '@crm-src/lib/store/invoicesSlice'

export const useInvoices = () => {
  const dispatch = useAppDispatch()
  const invoicesState = useAppSelector((state) => state.invoices)

  // Fetch all invoices with optional filters
  const getInvoices = useCallback(async (params?: InvoiceListFilters) => {
    try {
      const result = await dispatch(fetchInvoices(params || {})).unwrap()
      return result
    } catch (error) {
      console.error('Failed to fetch invoices:', error)
      throw error
    }
  }, [dispatch])

  // Create new invoice
  const createNewInvoice = useCallback(async (invoiceData: Partial<Invoice>, file?: File) => {
    try {
      const result = await dispatch(createInvoice({ invoiceData, file })).unwrap()
      return result
    } catch (error) {
      console.error('Failed to create invoice:', error)
      throw error
    }
  }, [dispatch])

  // Update existing invoice
  const updateExistingInvoice = useCallback(async (id: number, data: Partial<Invoice>, file?: File) => {
    try {
      const result = await dispatch(updateInvoice({ id, data, file })).unwrap()
      return result
    } catch (error) {
      console.error('Failed to update invoice:', error)
      throw error
    }
  }, [dispatch])

  // Delete invoice
  const removeInvoice = useCallback(async (id: number) => {
    try {
      await dispatch(deleteInvoice(id)).unwrap()
      return true
    } catch (error) {
      console.error('Failed to delete invoice:', error)
      throw error
    }
  }, [dispatch])

  // Bulk delete invoices
  const removeBulkInvoices = useCallback(async (ids: number[]) => {
    try {
      const result = await dispatch(bulkDeleteInvoices(ids)).unwrap()
      return result
    } catch (error) {
      console.error('Failed to bulk delete invoices:', error)
      throw error
    }
  }, [dispatch])

  // Upload invoice file
  const uploadFile = useCallback(async (id: number, file: File) => {
    try {
      const result = await dispatch(uploadInvoiceFile({ id, file })).unwrap()
      return result
    } catch (error) {
      console.error('Failed to upload invoice file:', error)
      throw error
    }
  }, [dispatch])

  // Mark invoice as paid
  const markAsPaid = useCallback(async (id: number, paid_amount?: number, paid_date?: string) => {
    try {
      const result = await dispatch(markInvoiceAsPaid({ id, paid_amount, paid_date })).unwrap()
      return result
    } catch (error) {
      console.error('Failed to mark invoice as paid:', error)
      throw error
    }
  }, [dispatch])

  // Mark invoice as unpaid
  const markAsUnpaid = useCallback(async (id: number) => {
    try {
      const result = await dispatch(markInvoiceAsUnpaid({ id })).unwrap()
      return result
    } catch (error) {
      console.error('Failed to mark invoice as unpaid:', error)
      throw error
    }
  }, [dispatch])

  // Get invoice statistics
  const getInvoiceStats = useCallback(async (params?: { document_type?: 'invoice' | 'quote' }) => {
    try {
      const result = await dispatch(fetchInvoiceStats(params || {})).unwrap()
      return result
    } catch (error) {
      console.error('Failed to fetch invoice stats:', error)
      throw error
    }
  }, [dispatch])

  // Utility functions
  const clearInvoicesCache = useCallback(() => {
    dispatch(clearCache())
  }, [dispatch])

  const clearInvoicesError = useCallback(() => {
    dispatch(clearError())
  }, [dispatch])

  // Computed values
  const isLoading = invoicesState.isLoading
  const error = invoicesState.error
  const invoices = invoicesState.invoices
  const stats = invoicesState.stats
  const pagination = invoicesState.pagination

  return {
    // State
    isLoading,
    error,
    invoices,
    stats,
    pagination,

    // Actions
    getInvoices,
    createNewInvoice,
    updateExistingInvoice,
    removeInvoice,
    removeBulkInvoices,
    uploadFile,
    markAsPaid,
    markAsUnpaid,
    getInvoiceStats,

    // Utilities
    clearInvoicesCache,
    clearInvoicesError,
  }
}



